import Cookies from "js-cookie";
import { axiosArisChatInstance } from "../AxiosConfiguration";

/**
 * Dialogue Creation Intelligence (DCI) — Capture (aris-ai
 * `/ARIS/v4/dialogue-creations`, raw, no envelope).
 *
 * Contract: aris-ai `docs/ARIS_PLATFORM/Aris Dialogue Intelligence/
 * DCI_API_INTEGRATION_GUIDE.md` and MOBILE_INTEGRATION_GUIDE.md §7.3/§7.3a.
 *
 * - Everything is behind the member flag `dci_v26`: while it is off every
 *   route 404s, which means "not available", never "failed".
 * - The server owns the state machine. The client renders `status` +
 *   `progress_step` from `GET /{id}` and only moves state through these calls.
 * - Every refusal is `{detail: {code, message}}` — key the UI on `code`.
 */

const BASE = "/ARIS/v4/dialogue-creations";

/** The spec-§32 statuses the screens switch on. */
export const CREATION_STATUS = {
  DRAFT: "DRAFT",
  CAPTURING: "CAPTURING",
  UPLOADING: "UPLOADING",
  INPUT_COMPLETE: "INPUT_COMPLETE",
  ANALYZING: "ANALYZING",
  NEEDS_SOURCE_CONFIRMATION: "NEEDS_SOURCE_CONFIRMATION",
  SUMMARY_READY: "SUMMARY_READY",
  SUMMARY_CONFIRMED: "SUMMARY_CONFIRMED",
  READY: "READY",
  FAILED_RECOVERABLE: "FAILED_RECOVERABLE",
  FAILED_TERMINAL: "FAILED_TERMINAL",
};

export const INPUT_METHOD = {
  TEXT: "TEXT",
  DOCUMENT: "DOCUMENT",
  PHOTO: "PHOTO",
  RECORDING: "RECORDING",
};

/** `{code, message, status}` off any DCI refusal. */
export const dciError = (e) => {
  const detail = e?.response?.data?.detail;
  return {
    status: e?.response?.status ?? null,
    code: (detail && typeof detail === "object" && detail.code) || null,
    message: (detail && typeof detail === "object" && detail.message) || null,
  };
};

/** True when the refusal means "DCI is off for this member" (or not theirs). */
export const isNotAvailable = (e) => e?.response?.status === 404;

const newIdempotencyKey = () =>
  `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * `POST /dialogue-creations` → `{creation}`. The idempotency key is stable
 * across retries of the same tap, so a retry returns the same creation.
 */
export const createCreation = async (inputMethod, idempotencyKey = newIdempotencyKey()) => {
  const res = await axiosArisChatInstance.post(BASE, {
    input_method: inputMethod,
    idempotency_key: idempotencyKey,
    client_platform: "web",
  });
  return res?.data?.creation ?? res?.data ?? null;
};

/**
 * `GET /dialogue-creations/vocabulary` — the Confirm Source dropdown lists
 * (`source_roles`, `source_formats`, `relationships`, each `{value, label,
 * requires_free_text?}`). Never hardcoded. Also the cheapest probe of the
 * `dci_v26` flag: a 404 here means Capture is not available to this member.
 */
export const getVocabulary = async () => {
  const res = await axiosArisChatInstance.get(`${BASE}/vocabulary`);
  return res?.data ?? null;
};

/** `GET /{id}` → the creation: `status`, `progress_step`, `provenance`, `analysis`, … */
export const getCreation = async (id) => {
  const res = await axiosArisChatInstance.get(`${BASE}/${encodeURIComponent(id)}`);
  return res?.data?.creation ?? res?.data ?? null;
};

/** `POST /{id}/artifacts` — one file, multipart field `file`. */
export const uploadArtifact = async (id, file, onProgress) => {
  const form = new FormData();
  form.append("file", file);
  const res = await axiosArisChatInstance.post(
    `${BASE}/${encodeURIComponent(id)}/artifacts`,
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) onProgress(evt.loaded / evt.total);
      },
    }
  );
  return res?.data ?? null;
};

/** `POST /{id}/artifacts/text` — `{content}`, ≤ 50,000 characters. */
export const uploadText = async (id, content) => {
  const res = await axiosArisChatInstance.post(
    `${BASE}/${encodeURIComponent(id)}/artifacts/text`,
    { content }
  );
  return res?.data ?? null;
};

/** `POST /{id}/finalize-input` — closes capture and starts analysis. */
export const finalizeInput = async (id) => {
  const res = await axiosArisChatInstance.post(`${BASE}/${encodeURIComponent(id)}/finalize-input`);
  return res?.data?.creation ?? res?.data ?? null;
};

/**
 * `PATCH /{id}/provenance` — only changed fields, plus `confirm: true` for
 * "Looks Good" (advances to SUMMARY_READY).
 */
export const patchProvenance = async (id, fields) => {
  const res = await axiosArisChatInstance.patch(
    `${BASE}/${encodeURIComponent(id)}/provenance`,
    fields
  );
  return res?.data?.creation ?? res?.data ?? null;
};

/** `PATCH /{id}/analysis` — edits and/or `confirm: true` (→ SUMMARY_CONFIRMED). */
export const patchAnalysis = async (id, fields) => {
  const res = await axiosArisChatInstance.patch(
    `${BASE}/${encodeURIComponent(id)}/analysis`,
    fields
  );
  return res?.data?.creation ?? res?.data ?? null;
};

/** `POST /{id}/generate-dialogue` → 202 `{creation, scheduled}`; poll to READY. */
export const generateDialogue = async (id) => {
  const res = await axiosArisChatInstance.post(
    `${BASE}/${encodeURIComponent(id)}/generate-dialogue`
  );
  return res?.data ?? null;
};

/** `DELETE /{id}` — the full cascade. A repeat is 404: it is genuinely gone. */
export const deleteCreation = async (id) => {
  const res = await axiosArisChatInstance.delete(`${BASE}/${encodeURIComponent(id)}`);
  return res?.data ?? null;
};

/**
 * `POST|DELETE /{id}/dates/{i}/reminder` — accept / decline one source date
 * as a reminder (guide §7.3a). Only from SUMMARY_CONFIRMED on; idempotent.
 */
export const acceptDateReminder = async (id, index) => {
  const res = await axiosArisChatInstance.post(
    `${BASE}/${encodeURIComponent(id)}/dates/${index}/reminder`
  );
  return res?.data ?? null;
};

export const declineDateReminder = async (id, index) => {
  const res = await axiosArisChatInstance.delete(
    `${BASE}/${encodeURIComponent(id)}/dates/${index}/reminder`
  );
  return res?.data ?? null;
};

/**
 * `POST /{id}/recording/start` with `{consent_acknowledged}` → `{recording:
 * {format: "pcm16", sample_rate: 16000, max_seconds, stream_path}}`. A first
 * recording without consent is `412 RECORDING_CONSENT_REQUIRED`.
 */
export const startRecording = async (id, consentAcknowledged = false) => {
  const res = await axiosArisChatInstance.post(
    `${BASE}/${encodeURIComponent(id)}/recording/start`,
    { consent_acknowledged: consentAcknowledged }
  );
  return res?.data?.recording ?? res?.data ?? null;
};

/**
 * The recording socket URL: aris-ai's host with ws(s)://, the server-given
 * `stream_path`, and the member token in the query string.
 */
export const recordingSocketUrl = (streamPath) => {
  const base = (process.env.REACT_APP_BASE_URL_CHAT_ARIS || "").replace(/^http/, "ws");
  const token = Cookies.get("idtoken") || "";
  return `${base}${streamPath}?token=${encodeURIComponent(token)}`;
};
